import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useSEO } from '../../context/SEOContext';
import { API_ENVIRONMENT } from '../../config/api.config';

const PLATFORM_NAME = 'My Pet Groomer';

export interface SEOProps {
  shopName?: string;
  shopLocation?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  companyLogo?: string;
  structuredData?: object;
}

export const SEO: React.FC<SEOProps> = ({
  shopName: propShopName,
  shopLocation,
  ogImage,
  ogType = 'website',
  noIndex = false,
  companyLogo: propCompanyLogo,
  structuredData,
}) => {
  const location = useLocation();
  const seoContext = useSEO();

  const shopName = propShopName || seoContext.companyName;
  const companyLogo = propCompanyLogo || seoContext.companyLogo;

  const baseUrl = window.location.origin;
  const canonicalUrl = `${baseUrl}${location.pathname}`;

  // Title format: "{ShopName} - Book Online | My Pet Groomer"
  // Or with location: "{ShopName} in {Location} - Book Online | My Pet Groomer"
  const buildTitle = () => {
    if (shopName && shopLocation) {
      return `${shopName} in ${shopLocation} - Book Online | ${PLATFORM_NAME}`;
    }
    if (shopName) {
      return `${shopName} - Book Online | ${PLATFORM_NAME}`;
    }
    if (shopLocation) {
      return `${shopLocation} - Book Online | ${PLATFORM_NAME}`;
    }
    return `Book Pet Grooming Online | ${PLATFORM_NAME}`;
  };

  const fullTitle = buildTitle();

  // Meta description format
  const buildDescription = () => {
    if (!shopName) {
      return `Official online booking for pet grooming services. Powered by ${PLATFORM_NAME} - the secure platform for local pet services. Check availability and book instantly.`;
    }

    const locationPart = shopLocation ? ` in ${shopLocation}` : '';
    return `Official online booking for ${shopName}${locationPart}. Powered by ${PLATFORM_NAME} - the secure platform for local pet services. Check availability and book instantly.`;
  };

  const metaDescription = buildDescription();

  const ogImageUrl = ogImage || companyLogo;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* The staging bundle is never indexable, whichever page renders last.
          The old App-level force (`<SEO noIndex />` at the root) was silently
          defeated by exactly this component: react-helmet is last-wins, and
          every page that rendered its own <SEO> put "index, follow" back. A
          rule meant to be unconditional has to live where the tag is made. */}
      <meta
        name="robots"
        content={API_ENVIRONMENT === 'staging' || noIndex ? "noindex, nofollow" : "index, follow"}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content={PLATFORM_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
